/**
 * useAddRoadmapItem
 *
 * Local state + submit orchestration for the Add Roadmap Item (Add idea) page.
 *
 * Mirrors the Flutter business logic at
 * `lib/presentation/screens/roadmap/add_roadmap_item_screen.dart:59-135`:
 *   1. Early-return guard: `if (_isSubmitting || !_canSubmit) return;`.
 *   2. Confirm    → show the confirm sheet; abort unless `confirmed == true`.
 *   3. On confirm → set submitting, build + submit the item via the use case.
 *   4. Always     → clear submitting (tear the loading overlay down *before*
 *                   opening the next sheet, per the `useContact` ordering note).
 *   5. On failure → invoke `onError` (page opens the error info sheet).
 *   6. On success → emit `ROADMAP_ITEM_ADDED` (so `RoadmapPage` reloads), then
 *                   invoke `onSuccess` (page opens the success sheet + navigates
 *                   back).
 *
 * Clean-Architecture note: this hook owns state + orchestration only. It does
 * NOT decide a presentation surface — the page wires `ConfirmSheet.show` /
 * `InfoSheet.show` + `Navigator.goBack` into the three callbacks below and
 * supplies the current user. The hook stays presentation-surface-agnostic and
 * builds no copy strings.
 *
 * The `ROADMAP_ITEM_ADDED` emit fires on `success` *before* `onSuccess()`
 * (which shows the sheet + navigates back), so by the time `RoadmapPage` is
 * revealed the event has already been delivered to its listener —
 * order-equivalent to Flutter's pop-then-reload (Flutter sets `_submitted`,
 * shows the success sheet, then pops, and the parent re-reads on pop).
 */
import { useCallback, useState } from 'react';
import { addRoadmapItemUseCase } from '@/domain/usecases/roadmap/addRoadmapItemUseCase';
import type { RoadmapItemAuthor } from '@/domain/input/roadmap/roadmapItemAuthor';
import { eventBus, APP_EVENT_KEYS } from '@/infrastructure/eventBus';
import { appLogger } from '@/domain/services/analytics/appLogger';

export interface UseAddRoadmapItemOptions {
    /**
     * The author to stamp on the new item (Flutter reads
     * `currentUser.firebaseId` / `currentUser.name`). The page supplies it.
     */
    currentUser: RoadmapItemAuthor;
    /**
     * Called once the submit guard passes. Resolve `true` to proceed with the
     * POST, `false` (or dismiss) to abort. The page typically wires a confirm
     * sheet here.
     */
    onConfirmSubmit: () => Promise<boolean>;
    /**
     * Called after a successful submit (and after the `ROADMAP_ITEM_ADDED`
     * event is emitted). The page typically opens the success info sheet and,
     * on its close, navigates back.
     */
    onSuccess: () => void | Promise<void>;
    /**
     * Called after a failed submit. The page typically opens the error info
     * sheet.
     */
    onError: () => void | Promise<void>;
}

export interface UseAddRoadmapItemResult {
    title: string;
    description: string;
    isSubmitting: boolean;
    canSubmit: boolean;
    setTitle: (value: string) => void;
    setDescription: (value: string) => void;
    submit: () => Promise<void>;
}

export const useAddRoadmapItem = ({
    currentUser,
    onConfirmSubmit,
    onSuccess,
    onError,
}: UseAddRoadmapItemOptions): UseAddRoadmapItemResult => {
    const [title, setTitle] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Exact Flutter `_canSubmit` predicate
    // (add_roadmap_item_screen.dart:29-32): not submitting AND both fields
    // non-empty. Flutter uses `isNotEmpty` (untrimmed), so we mirror with
    // `length > 0` rather than a trimmed check.
    const canSubmit = !isSubmitting && title.length > 0 && description.length > 0;

    const submit = useCallback(async () => {
        // Step 1: early-return guard (add_roadmap_item_screen.dart:60-62).
        if (isSubmitting || !canSubmit) {
            return;
        }

        // Step 2: confirm → caller resolves `false` to cancel
        // (add_roadmap_item_screen.dart:73-79).
        const confirmed = await onConfirmSubmit();
        if (!confirmed) return;

        // Step 3-4: submit, then clear submitting *before* invoking the next
        // surface callback. The loading overlay must be torn down before the
        // page opens its success/error sheet (per the `useContact` ordering
        // note) so the overlay does not paint on top of the next sheet.
        setIsSubmitting(true);
        let success: boolean;
        try {
            success = await addRoadmapItemUseCase.execute(title, description, currentUser);
        } catch (error) {
            // The data layer already catches and returns `false`; this catch is
            // defensive — log and report `false` so the loading overlay never
            // gets stranded.
            appLogger.error('Error adding roadmap item', 'useAddRoadmapItem.submit', {
                error: error as Error,
            });
            success = false;
        } finally {
            setIsSubmitting(false);
        }

        if (!success) {
            // Step 5: failure → caller-owned error surface
            // (add_roadmap_item_screen.dart:115-122).
            await onError();
            return;
        }

        // Step 6: success → emit the reload signal, then caller-owned success
        // surface + navigate back (add_roadmap_item_screen.dart:123-134).
        eventBus.emit(APP_EVENT_KEYS.ROADMAP_ITEM_ADDED, {});
        await onSuccess();
    }, [isSubmitting, canSubmit, title, description, currentUser, onConfirmSubmit, onSuccess, onError]);

    return {
        title,
        description,
        isSubmitting,
        canSubmit,
        setTitle,
        setDescription,
        submit,
    };
};
